import React, { useEffect, useState } from "react";
import { useApp } from "../../context/AppContext";

function QuestionInput({ q, id, onChange }) {
  if (q.type === "multiselect" && q.options.length) {
    return (
      <div className="question-choices">
        {q.options.map((option, i) => (
          <div className="form-check" key={option}>
            <input
              className="form-check-input"
              type="checkbox"
              id={`${id}-${i}`}
              checked={q.choices.includes(option)}
              onChange={(e) =>
                onChange({ choices: e.target.checked ? [...q.choices, option] : q.choices.filter((c) => c !== option) })
              }
            />
            <label className="form-check-label small" htmlFor={`${id}-${i}`}>
              {option}
            </label>
          </div>
        ))}
      </div>
    );
  }
  if (q.options.length) {
    return (
      <select id={id} className="form-select form-select-sm" value={q.answer} onChange={(e) => onChange({ answer: e.target.value })}>
        <option value="">Choose…</option>
        {q.options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }
  if (q.type === "textarea") {
    return (
      <textarea id={id} className="form-control form-control-sm" rows={5} value={q.answer} onChange={(e) => onChange({ answer: e.target.value })} />
    );
  }
  return (
    <input
      id={id}
      className="form-control form-control-sm"
      value={q.answer}
      placeholder={q.manyOptions ? "Type it exactly as it appears in the form's list" : ""}
      onChange={(e) => onChange({ answer: e.target.value })}
    />
  );
}

// The application form's own questions, answered ahead of time. The extension fills these in on the page.
function QuestionsCard({ job }) {
  const { updateJob, toast } = useApp();
  const [draft, setDraft] = useState(job.questions);
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(job.questions), [job.id, job.questions]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(job.questions);
  const needsYou = draft.filter((q) => q.needsYou).length;

  const change = (index, changes) =>
    setDraft((list) => list.map((q, i) => (i === index ? { ...q, ...changes, needsYou: false, note: "" } : q)));

  const save = async () => {
    setSaving(true);
    const saved = await updateJob(job.id, { questions: draft });
    setSaving(false);
    if (saved) toast("Answers saved");
  };

  return (
    <div className="card mb-3">
      <div className="card-header-lite">
        <div>
          <h2 className="h6 mb-0">Application questions ({draft.length})</h2>
          <div className="small text-muted">
            Read from the application form and answered from your resume and profile. The extension fills these in when you open the
            application. Short answers you change here are remembered for the same question on later applications.
          </div>
        </div>
        {needsYou > 0 && (
          <span className="tag tag-warning flex-shrink-0">
            <i className="bi bi-exclamation-circle" /> {needsYou} need{needsYou === 1 ? "s" : ""} you
          </span>
        )}
      </div>
      <div className="card-body pt-0">
        {draft.map((q, i) => {
          const id = `question-${i}`;
          return (
            <div key={`${q.key}-${i}`} className={`question-item ${q.needsYou ? "needs-you" : ""}`}>
              <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                <label className="small fw-medium mb-0" htmlFor={id} style={{ whiteSpace: "pre-line", overflowWrap: "anywhere" }}>
                  {q.label}
                  {q.required && <span className="text-danger"> *</span>}
                </label>
                {q.needsYou && <span className="tag tag-warning flex-shrink-0">Needs you</span>}
              </div>
              {q.description && <div className="small text-muted mb-1">{q.description}</div>}
              {q.needsYou && q.note && <div className="small text-warning-emphasis mb-1">{q.note}</div>}
              <QuestionInput q={q} id={id} onChange={(changes) => change(i, changes)} />
            </div>
          );
        })}
      </div>
      {dirty && (
        <div className="settings-footer">
          <span className="small text-muted me-auto">Unsaved answers</span>
          <button className="btn btn-light btn-sm" onClick={() => setDraft(job.questions)} disabled={saving}>
            Undo
          </button>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
            {saving && <span className="spinner-border spinner-border-sm me-1" />}
            Save answers
          </button>
        </div>
      )}
    </div>
  );
}

export default QuestionsCard;
